/**
 * EmployerJobs — dedicated employer Jobs page (nav: Jobs). Answers "what positions am I
 * hiring for, and which ones need my attention?" over canonical jobs + job_applications +
 * invites via GET /api/jobs/my-posts/overview. No new job-management model. Funnel counts
 * reuse the job/stage drill-down routing into the Applications experience. Job-management
 * actions reuse existing endpoints (PUT /api/jobs/:id, Find Talent preselect, /jobs/post).
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Plus, Search, Briefcase, MapPin, Clock, Users, MessageSquareWarning, CalendarCheck,
  BadgeCheck, Trophy, Send, ArrowRight, Loader2, Power, Play, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  inactive: { label: 'Paused', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  closed: { label: 'Closed', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const SORTS = [
  { value: 'action_needed', label: 'Action Needed' },
  { value: 'newest', label: 'Newest' },
  { value: 'most_applicants', label: 'Most Applicants' },
  { value: 'last_activity', label: 'Last Activity' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Paused' },
  { value: 'closed', label: 'Closed' },
];

function daysOpen(iso) {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d < 0 ? 0 : d;
}
function fmtActivity(iso) {
  if (!iso) return 'No activity yet';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return 'Active today';
  if (d === 1) return 'Active yesterday';
  if (d < 30) return `Active ${d} days ago`;
  try { return `Active ${new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`; }
  catch { return 'No activity yet'; }
}

export default function EmployerJobs() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('action_needed');
  const [toggling, setToggling] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/jobs/my-posts/overview`, { withCredentials: true });
      setJobs(res.data || []);
    } catch {
      toast.error('Could not load your jobs');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(r => setUser(r.data)).catch(() => {});
  }, []);

  const toApplications = (jobId, stage) =>
    navigate(`/employer/dashboard?tab=applications&job=${jobId}${stage ? `&stage=${stage}` : ''}`);

  const toggleStatus = async (job, e) => {
    e.stopPropagation();
    const next = job.status === 'active' ? 'inactive' : 'active';
    setToggling(job.job_id);
    try {
      await axios.put(`${BACKEND_URL}/api/jobs/${job.job_id}`, { status: next }, { withCredentials: true });
      toast.success(next === 'active' ? 'Job resumed' : 'Job paused');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update job');
    } finally {
      setToggling(null);
    }
  };

  const visible = useMemo(() => {
    let list = [...jobs];
    if (statusFilter !== 'all') list = list.filter(j => (j.status || 'active') === statusFilter);
    const ql = query.trim().toLowerCase();
    if (ql) list = list.filter(j =>
      (j.title || '').toLowerCase().includes(ql) || (j.location || '').toLowerCase().includes(ql));

    const ts = (v) => (v ? new Date(v).getTime() : 0);
    if (sort === 'newest') {
      list.sort((a, b) => ts(b.posted_at) - ts(a.posted_at));
    } else if (sort === 'most_applicants') {
      list.sort((a, b) => (b.funnel?.applicants || 0) - (a.funnel?.applicants || 0));
    } else if (sort === 'last_activity') {
      list.sort((a, b) => ts(b.last_activity_at) - ts(a.last_activity_at));
    } else {
      // action_needed: jobs needing response first, ordered by oldest unresolved activity
      // (most urgent first); remaining jobs by most recent activity.
      const needs = list.filter(j => j.needs_response);
      const rest = list.filter(j => !j.needs_response);
      needs.sort((a, b) => ts(a.oldest_waiting) - ts(b.oldest_waiting));
      rest.sort((a, b) => ts(b.last_activity_at) - ts(a.last_activity_at));
      list = [...needs, ...rest];
    }
    return list;
  }, [jobs, statusFilter, query, sort]);

  const metricDefs = (f) => [
    { key: 'applicants', label: 'Applicants', value: f.applicants, icon: Users, stage: '' },
    { key: 'needs_response', label: 'Needs Response', value: f.needs_response, icon: MessageSquareWarning, stage: 'needs_response', accent: true },
    { key: 'interviewing', label: 'Interviewing', value: f.interviewing, icon: CalendarCheck, stage: 'interviewing' },
    { key: 'offers', label: 'Offers', value: f.offers, icon: BadgeCheck, stage: 'offers' },
    { key: 'hired', label: 'Hired', value: f.hired, icon: Trophy, stage: 'hired' },
  ];

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="employer-jobs-page">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Jobs</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Manage every position you&apos;re hiring for. Roles that need your attention rise to the top —
              <span className="font-medium text-amber-700"> Action Needed</span> means applicants are waiting on your response.
            </p>
          </div>
          <Button onClick={() => navigate('/jobs/post')} className="bg-brand hover:bg-brand-hover text-white h-10 px-5 rounded-lg font-semibold" data-testid="jobs-post-role-btn">
            <Plus className="w-4 h-4 mr-2" /> Post a Role
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-24" data-testid="jobs-loading"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
        ) : jobs.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-16 text-center mt-6" data-testid="jobs-empty-state">
            <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-7 h-7 text-slate-300" />
            </div>
            <p className="text-slate-700 font-medium mb-1">You haven&apos;t posted any roles yet</p>
            <p className="text-sm text-slate-400 mb-5">Post your first position to start receiving applications from qualified veterans.</p>
            <Button onClick={() => navigate('/jobs/post')} className="bg-brand hover:bg-brand-hover text-white rounded-lg" data-testid="jobs-empty-post-btn">
              <Plus className="w-4 h-4 mr-2" /> Post Your First Job
            </Button>
          </div>
        ) : (
          <>
            {/* Controls */}
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between bg-white rounded-xl border border-slate-200 p-3 mt-6 mb-4" data-testid="jobs-controls">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="jobs-search-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by title or location…"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/30"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1 bg-slate-100/80 rounded-lg p-1" data-testid="jobs-status-filters">
                  {STATUS_FILTERS.map(s => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setStatusFilter(s.value)}
                      data-testid={`jobs-status-filter-${s.value}`}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${statusFilter === s.value ? 'bg-white text-brand shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Sort</span>
                  <select
                    data-testid="jobs-sort-select"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 bg-white"
                  >
                    {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {visible.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-sm text-slate-500" data-testid="jobs-no-match">
                No jobs match your search or filters.
              </div>
            ) : (
              <div className="space-y-3" data-testid="jobs-list">
                {visible.map(job => {
                  const meta = STATUS_META[job.status] || STATUS_META.active;
                  const f = job.funnel || {};
                  const dOpen = daysOpen(job.posted_at);
                  return (
                    <Card
                      key={job.job_id}
                      onClick={() => navigate(`/jobs/${job.job_id}`)}
                      data-testid={`jobs-card-${job.job_id}`}
                      className={`group p-5 rounded-2xl border transition-all cursor-pointer hover:shadow-md ${job.needs_response ? 'border-amber-300 ring-1 ring-amber-100' : 'border-slate-200 hover:border-brand/40'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2.5 flex-wrap mb-1">
                            <h3 className="text-lg font-semibold text-slate-900 truncate group-hover:text-brand transition-colors" data-testid={`jobs-card-title-${job.job_id}`}>{job.title}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border flex-shrink-0 ${meta.cls}`} data-testid={`jobs-card-status-${job.job_id}`}>{meta.label}</span>
                            {job.needs_response && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[11px] font-semibold" data-testid={`jobs-card-attention-${job.job_id}`}>
                                <AlertTriangle className="w-3 h-3" /> {f.needs_response} need{f.needs_response === 1 ? 's' : ''} response
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{job.remote ? 'Remote' : (job.location || '—')}</span>
                            {job.job_type && <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{job.job_type.replace('_', ' ')}</span>}
                            {dOpen != null && <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="w-3 h-3" />{dOpen === 0 ? 'Posted today' : `${dOpen} day${dOpen === 1 ? '' : 's'} open`}</span>}
                            <span className="text-xs text-slate-400">{fmtActivity(job.last_activity_at)}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <Button onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${job.job_id}`); }} size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:border-brand hover:text-brand rounded-lg" data-testid={`jobs-open-${job.job_id}`}>
                            Open Job <ArrowRight className="w-3.5 h-3.5 ml-1" />
                          </Button>
                          <div className="flex items-center gap-2">
                            <Button onClick={(e) => { e.stopPropagation(); navigate(`/candidates/search?rankJob=${job.job_id}`); }} size="sm" variant="ghost" className="h-8 text-xs text-brand hover:bg-brand-light rounded-lg" data-testid={`jobs-find-talent-${job.job_id}`}>
                              <Search className="w-3.5 h-3.5 mr-1" /> Find Talent
                            </Button>
                            {job.status !== 'closed' && (
                              <Button onClick={(e) => toggleStatus(job, e)} disabled={toggling === job.job_id} size="sm" variant="ghost" className="h-8 text-xs text-slate-500 hover:text-slate-800 rounded-lg" data-testid={`jobs-toggle-status-${job.job_id}`}>
                                {toggling === job.job_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : job.status === 'active' ? <><Power className="w-3.5 h-3.5 mr-1" /> Pause</> : <><Play className="w-3.5 h-3.5 mr-1" /> Resume</>}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Funnel — clickable drill-downs into job-scoped applications */}
                      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid={`jobs-funnel-${job.job_id}`}>
                        {metricDefs(f).map(m => (
                          <button
                            key={m.key}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toApplications(job.job_id, m.stage); }}
                            data-testid={`jobs-funnel-${job.job_id}-${m.key}`}
                            className={`text-left rounded-xl border p-2.5 transition-colors hover:border-brand/40 hover:bg-brand-light/40 ${m.accent && m.value > 0 ? 'border-amber-200 bg-amber-50' : 'border-slate-100'}`}
                          >
                            <m.icon className={`w-3.5 h-3.5 mb-1 ${m.accent && m.value > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                            <div className={`text-lg font-bold ${m.accent && m.value > 0 ? 'text-amber-700' : 'text-slate-900'}`}>{m.value ?? 0}</div>
                            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide leading-tight">{m.label}</div>
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); navigate(`/candidates/search?rankJob=${job.job_id}`); }}
                          data-testid={`jobs-funnel-${job.job_id}-invited`}
                          className="text-left rounded-xl border border-slate-100 p-2.5 transition-colors hover:border-brand/40 hover:bg-brand-light/40"
                        >
                          <Send className="w-3.5 h-3.5 mb-1 text-slate-400" />
                          <div className="text-lg font-bold text-slate-900">{job.invited ?? 0}</div>
                          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide leading-tight">Invited</div>
                        </button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
